'use client';

import clsx from 'clsx';
import { useState } from 'react';

import { Matchup, MatchupCardLinkInterface, MatchupCardLinkTarget, Team } from '../../../types/ticker.types';

interface MatchLabelsProps {
	matchupUuid: string;
	matchLabels: string[];
	noOfColumns: number;
	bestOfThreeMatches: boolean;
	multiplePodsMatches: boolean;
}

function MatchLabels({ matchupUuid, matchLabels, noOfColumns, bestOfThreeMatches, multiplePodsMatches }: MatchLabelsProps) {
	return (
		<div
			className={clsx('grid grid-cols-[80px_1fr] gap-6 items-center', {
				'pt-1.5': bestOfThreeMatches && !multiplePodsMatches
			})}
		>
			<div /> {/* Filling div element */}
			<div
				className={`grid text-center text-sm font-semibold justify-center`}
				style={{ gridTemplateColumns: `repeat(${noOfColumns}, minmax(0, 1fr))` }}
			>
				{matchLabels.map((label) => {
					return (
						<div key={`matchup-${matchupUuid}-label-${label}`} className={clsx('text-gray-400 text-xs leading-[18px] w-full mr-2')}>
							{label}
						</div>
					);
				})}
			</div>
		</div>
	);
}

interface TeamRowProps {
	matchupUuid: string;
	team: Team;
	oppositeTeam: Team;
	noOfColumns: number;
	bestOfThreeMatches: boolean;
	multiplePodsMatches: boolean;
	teamNumber: string;
	isWinner: boolean;
}

function TeamRow({ matchupUuid, team, oppositeTeam, noOfColumns, bestOfThreeMatches, multiplePodsMatches, teamNumber, isWinner }: TeamRowProps) {
	const matchesWonInEachPod = multiplePodsMatches
		? team.podMatches.map((podMatch) => ({ podUuid: podMatch.podUuid, matchesWonInPod: podMatch.matchesWon }))
		: [];
	const oppositeTeamMatchesWonInEachPod = multiplePodsMatches
		? oppositeTeam.podMatches.map((podMatch) => ({ podUuid: podMatch.podUuid, matchesWonInPod: podMatch.matchesWon }))
		: [];

	const numberOfMatchesInSinglePod = !multiplePodsMatches ? (team?.podMatches[0]?.matches?.length ?? 1) : 0;

	return (
		<div
			className={clsx('grid grid-cols-[80px_1fr] gap-6 items-center', {
				'bg-[#EDFCF2]': isWinner,
				'py-1': bestOfThreeMatches && !multiplePodsMatches
			})}
		>
			<div className="flex flex-row items-center gap-1 pl-1">
				<img src={team.logo} alt={`${team.name} logo`} className="size-6 rounded-full overflow-hidden object-contain bg-white" />
				<span
					className={clsx('font-bold text-gray-900', {
						'text-base': bestOfThreeMatches && !multiplePodsMatches,
						'text-xs leading-[18px]': !bestOfThreeMatches || multiplePodsMatches
					})}
				>
					{team.name}
				</span>
			</div>

			<div className={`grid text-center text-sm font-semibold`} style={{ gridTemplateColumns: `repeat(${noOfColumns}, minmax(0, 1fr))` }}>
				{multiplePodsMatches ? (
					<>
						{matchesWonInEachPod.map((pod) => {
							const opponentScore = oppositeTeamMatchesWonInEachPod.find((m) => m.podUuid === pod.podUuid)?.matchesWonInPod ?? null;

							const displayValue =
								pod.matchesWonInPod === 0 && opponentScore !== null && opponentScore > 0
									? '0'
									: pod.matchesWonInPod === 0 && !opponentScore
										? '-'
										: (pod.matchesWonInPod ?? '-');

							const shouldMakeBold = pod.matchesWonInPod >= 2;

							return (
								<div
									key={`matchup-${matchupUuid}-team-row-${teamNumber}-pod-${pod.podUuid}`}
									className="flex flex-row justify-center"
								>
									<div className="flex flex-row justify-center">
										<div className="flex flex-row items-center">
											<div
												className={clsx('text-gray-900 w-4 text-base m-1.5', {
													'font-medium': !shouldMakeBold,
													'font-bold': shouldMakeBold
												})}
											>
												{displayValue}
											</div>
										</div>
									</div>
								</div>
							);
						})}
					</>
				) : (
					team.podMatches[0]?.matches?.map((match, matchIndex) => {
						return (
							<div key={`matchup-${matchupUuid}-team-row-${teamNumber}-match-${match.uuid}`} className="flex flex-row justify-center">
								<div className="flex flex-row justify-center">
									{match.gameScores.map((score, gameIndex) => {
										const opposingScores = oppositeTeam.podMatches[0]?.matches.find((m) => m.uuid === match.uuid)?.gameScores;

										const opponentScore = opposingScores?.[gameIndex] ?? null;
										const displayValue =
											score === 0 && opponentScore !== null && opponentScore > 0
												? '0'
												: score === 0 && !opponentScore
													? '-'
													: (score ?? '-');

										const minPointsToWin = team.podMatches[0]?.matches[0]?.minPointsToWin || 11;
										const scoreFormatGame1WinBy = team.podMatches[0]?.matches[0]?.scoreFormatGame1WinBy || 2;

										const shouldMakeBold = score >= minPointsToWin && score - (opponentScore || 0) >= scoreFormatGame1WinBy;

										return (
											<div
												key={`matchup-${matchupUuid}-team-row-${teamNumber}-match-${match.uuid}-${gameIndex}`}
												className="flex flex-row items-center"
											>
												<div
													className={clsx('text-gray-900', {
														'w-[14px] text-sm': bestOfThreeMatches,
														'w-4 text-base m-1.5': !bestOfThreeMatches,
														'font-medium': !shouldMakeBold,
														'font-bold': shouldMakeBold
													})}
												>
													{displayValue}
												</div>
												{bestOfThreeMatches && gameIndex !== 2 && matchIndex !== numberOfMatchesInSinglePod - 1 ? (
													<div className="mx-0.5">&#183;</div>
												) : null}
											</div>
										);
									})}
								</div>
								{bestOfThreeMatches && matchIndex !== numberOfMatchesInSinglePod - 1 ? <div className="mx-1">|</div> : null}
							</div>
						);
					})
				)}
			</div>
		</div>
	);
}

interface TeamLeaguesMatchupCardProps {
	matchup: Matchup;
	noOfColumns: number;
	className?: string;
	reverseTeamRows?: boolean;
	links?: MatchupCardLinkInterface;
}

export default function TeamLeaguesMatchupCard({ matchup, noOfColumns, className, reverseTeamRows, links }: TeamLeaguesMatchupCardProps) {
	const multiplePodsMatches = matchup.teamOne.podMatches.length > 1;
	const bestOfThreeMatches = matchup.teamOne.podMatches[0]?.matches[0]?.gameScores.length === 3 ? true : false;

	const [showLinks, setShowLinks] = useState(false);

	// All link logic lives in the ticker formatters — the card just renders what it's given.
	const hasSomeLink = Boolean(links?.liveURL || links?.archivedURL || links?.highlightsURL);

	const linkItemClasses = 'w-full flex items-center justify-center text-gray-700 hover:text-experiment h-full text-sm font-medium not-italic';

	const linkItems: { key: string; href: string; target: MatchupCardLinkTarget; label: string }[] = [];

	if (links?.liveURL) {
		linkItems.push({ key: 'live', href: links.liveURL, target: links.liveURLTarget || '_blank', label: links.liveURLLabel || 'Watch Live' });
	}

	if (links?.archivedURL) {
		linkItems.push({
			key: 'archived',
			href: links.archivedURL,
			target: links.archivedURLTarget || '_blank',
			label: links.archivedURLLabel || 'Watch Replay'
		});
	}

	if (links?.highlightsURL) {
		linkItems.push({
			key: 'highlights',
			href: links.highlightsURL,
			target: links.highlightsURLTarget || '_blank',
			label: links.highlightsURLLabel || 'Watch Highlights'
		});
	}

	if (links?.matchupStatsURL) {
		linkItems.push({
			key: 'stats',
			href: links.matchupStatsURL,
			target: links.matchupStatsURLTarget || '_self',
			label: links.matchupStatsURLLabel || 'Results'
		});
	}

	const card = (
		<div
			id={`matchup-${matchup.uuid}`}
			onMouseEnter={() => hasSomeLink && setShowLinks(true)}
			onMouseLeave={() => hasSomeLink && setShowLinks(false)}
			className={clsx('relative w-full flex flex-col rounded-[4px] pb-6 border border-solid border-gray-300 shadow-sm', className)}
		>
			{hasSomeLink && showLinks ? (
				<div className="bg-gray-50 z-50 absolute top-0 left-0 flex flex-col justify-evenly items-center w-full h-full rounded overflow-hidden">
					{linkItems.map((item, index) => (
						<a
							key={`matchup-${matchup.uuid}-link-${item.key}`}
							href={item.href}
							target={item.target}
							className={clsx(linkItemClasses, { 'border-t border-solid border-gray-300': index > 0 })}
						>
							{item.label}
						</a>
					))}
				</div>
			) : null}
			<div
				className={clsx('w-full px-1.5 text-gray-900 font-semibold', {
					'py-1 text-xs leading-[18px]': bestOfThreeMatches && !multiplePodsMatches,
					'py-[3px] text-sm': !bestOfThreeMatches || multiplePodsMatches
				})}
			>
				<span>{matchup.time}</span>
				{` `}
				<span>{matchup.timezone}</span>
			</div>
			<div>
				<MatchLabels
					matchupUuid={matchup.uuid}
					matchLabels={matchup.matchLabels}
					noOfColumns={noOfColumns}
					bestOfThreeMatches={bestOfThreeMatches}
					multiplePodsMatches={multiplePodsMatches}
				/>
				{reverseTeamRows ? (
					<>
						<TeamRow
							matchupUuid={matchup.uuid}
							team={matchup.teamTwo}
							oppositeTeam={matchup.teamOne}
							noOfColumns={noOfColumns}
							bestOfThreeMatches={bestOfThreeMatches}
							multiplePodsMatches={multiplePodsMatches}
							teamNumber="2"
							isWinner={matchup.winner === 'team2'}
						/>
						<TeamRow
							matchupUuid={matchup.uuid}
							team={matchup.teamOne}
							oppositeTeam={matchup.teamTwo}
							noOfColumns={noOfColumns}
							bestOfThreeMatches={bestOfThreeMatches}
							multiplePodsMatches={multiplePodsMatches}
							teamNumber="1"
							isWinner={matchup.winner === 'team1'}
						/>
					</>
				) : (
					<>
						<TeamRow
							matchupUuid={matchup.uuid}
							team={matchup.teamOne}
							oppositeTeam={matchup.teamTwo}
							noOfColumns={noOfColumns}
							bestOfThreeMatches={bestOfThreeMatches}
							multiplePodsMatches={multiplePodsMatches}
							teamNumber="1"
							isWinner={matchup.winner === 'team1'}
						/>
						<TeamRow
							matchupUuid={matchup.uuid}
							team={matchup.teamTwo}
							oppositeTeam={matchup.teamOne}
							noOfColumns={noOfColumns}
							bestOfThreeMatches={bestOfThreeMatches}
							multiplePodsMatches={multiplePodsMatches}
							teamNumber="2"
							isWinner={matchup.winner === 'team2'}
						/>
					</>
				)}
			</div>
			{matchup.sponsors?.logoUrl ? (
				<div className="absolute bottom-1 right-1.5 flex items-center justify-end">
					<img
						src={matchup.sponsors.logoUrl}
						alt={matchup.sponsors.serviceName || 'Service logo'}
						className="h-4 max-w-[96px] object-contain"
					/>
				</div>
			) : null}
		</div>
	);

	// Card owns its own link: when there are hover links, the overlay handles
	// navigation; otherwise wrap the whole card in the matchup link.
	if (hasSomeLink || !links?.matchupStatsURL) {
		return card;
	}

	return (
		<a href={links.matchupStatsURL} target={links.matchupStatsURLTarget || '_self'} className="w-full hover:opacity-80">
			{card}
		</a>
	);
}
